/**
 * Split a stored 24-hour time into the modal's hour / minute / AM-PM selects.
 *
 * `notes-c.time_` is "HH:MM:SS" (or "HH:MM"); the picker holds three separate
 * strings. Anything that prefills `time_` has to fill these too, or the picker
 * shows its default 09:00 AM while the form holds something else — and the
 * moment the admin touches any of the three selects, the picker's value wins
 * and the real time is lost.
 *
 * Returns null when there is nothing to parse, so callers can leave their
 * default in place.
 */
export function to12HourParts(value) {
  const text = String(value ?? "").trim();
  if (!text) return null;

  // Accept "14:30", "14:30:00" and "2:30 PM" — the last because notes-c has
  // historically held both 24h and 12h strings.
  const meridiem = text.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)$/i);
  let hour24;
  let minute;
  if (meridiem) {
    hour24 = parseInt(meridiem[1], 10) % 12;
    minute = parseInt(meridiem[2], 10);
    if (meridiem[3].toUpperCase() === "PM") hour24 += 12;
  } else {
    const parts = text.match(/^(\d{1,2}):(\d{2})/);
    if (!parts) return null;
    hour24 = parseInt(parts[1], 10);
    minute = parseInt(parts[2], 10);
  }
  if (!Number.isFinite(hour24) || !Number.isFinite(minute)) return null;
  if (hour24 < 0 || hour24 > 23 || minute < 0 || minute > 59) return null;

  return {
    hours: String(hour24 % 12 === 0 ? 12 : hour24 % 12).padStart(2, "0"),
    minutes: String(minute).padStart(2, "0"),
    ampm: hour24 >= 12 ? "PM" : "AM",
  };
}

/**
 * Minute options for the picker: the usual quarter hours, plus whatever the
 * current value is if it isn't one of them.
 *
 * A clinic on a 20-minute grid produces slots at :20 and :40. Snapping those
 * to the nearest quarter would silently move the appointment away from the
 * time the patient actually requested, so the exact value is offered instead.
 */
export function minuteOptions(current) {
  const base = ["00", "15", "30", "45"];
  const value = String(current ?? "").padStart(2, "0");
  if (!/^\d{2}$/.test(value) || base.includes(value)) return base;
  return [...base, value].sort();
}
